import React from 'react';
import { useSelector } from 'react-redux';

import { getEndpointForService } from 'reducers/configs/serviceEnpoints';
import { getClientCode } from 'reducers/Login';

// Typescript does not have type information for the EventSource API
declare const EventSource: any;

/**
 * Workaround to detect when we have reached a 404 page
 *
 * For some unknown reason, when requesting an eventstream for a page that does not exist
 * the API does not give a regular 404 response nor a regular stream
 * but instead gives us a stream that IMMEDIATELY emits the html of a 404 page as content
 * and then immediately closes, triggering the browser to open a new request
 * This repeats ad infinitum.
 *
 * This behaviour makes no sense, but until the API is fixed it means we need to have
 * special detection for it and manually close the connection to prevent the loop
 */
const REGEX_IS_404_RESPONSE_DATA = /^<!doctype html>\n\n<html lang="en">/;

/**
 * Decodes content sent via a goERP event stream.
 *
 * ## When some text is pushed to a goERP event stream, goERP packages it in the following way
 * ```
 *   { type: "example", payload: "This is an example of publishing some JSON content in goERP" }
 * ```
 * 1. The content is XML encoded
 * ```
 *   { type: &#34;example&#34;, payload: &#34;This is an example of publishing some JSON content in goERP&#34; }
 * ```
 * 2. The content is JSON encoded as a string
 * ```
 *   "{ type: \u0026#34;example\u0026#34;, payload: \u0026#34;This is an example of publishing some JSON content in goERP\u0026#34; }"
 * ```
 * 3. This string is put in an object `{ data: ..., isFinished: true}` and emitted as a 'content' event
 * ```
 * event: content
 * data: { "data": "{ type: \u0026#34;example\u0026#34;, payload: \u0026#34;This is an example of publishing some JSON content in goERP\u0026#34; }", "isFinished": true}
 * ```
 *
 * NB: The goERP stream also starts with a placeholder event
 * ```
 * event: content
 * data: {"data": "loading", "isFinished": false }
 * ```
 *
 * ## Therefore we parse it in the opposite order
 * ```
 * event: content
 * data: {"data": "loading", "isFinished": false }
 * ```
 * ```
 * event: content
 * data: { "data": "{ type: \u0026#34;example\u0026#34;, payload: \u0026#34;This is an example of publishing some JSON content in goERP\u0026#34; }", "isFinished": true}
 * ```
 * 1. Parse the event data as json
 * ```json
 *   { "data": "{ type: \u0026#34;example\u0026#34;, payload: \u0026#34;This is an example of publishing some JSON content in goERP\u0026#34; }", "isFinished": true}
 * ```
 * ```
 *   {"data": "loading", "isFinished": false }
 * ```
 * 2. Check if the data is loading. If so, return it as parsed
 * ```
 * no
 * ```
 * ```
 *   {"data": "loading", "isFinished": false }
 * ```
 * 3. Otherwise, decode the unicode escapes from the string
 * ```
 *   "{ type: &#34;example&#34;, payload: &#34;This is an example of publishing some JSON content in goERP&#34; }"
 * ```
 * 4. Then decode the html escapes from the string
 * ```
 *   { type: "example", payload: "This is an example of publishing some JSON content in goERP" }
 * ```
 * 5. Then parse the string as json
 * ```json
 *   { type: "example", payload: "This is an example of publishing some JSON content in goERP" }
 * ```
 * @param data
 */
function decodeGoErpContent(data: string): object[] {
  const eventStreamData = JSON.parse(data);

  if (eventStreamData.data === 'loading') return [];

  const ta = document.createElement('textarea');
  ta.innerHTML = eventStreamData.data;
  const jsonData = ta.value;

  const action = JSON.parse(jsonData);

  return [action];
}

/**
 * Subscribe to the given goERP stream, receiving content updates into the onMsg callback
 *
 * Given the following "page" that just prints out whatever it receives as the "name" in an object
 * ```html
 * <!-- goERP 'action' page -->
 * { "name": {{.Data.Stream}} }
 * ```
 * And this page which emits a time string
 * ```html
 * <!-- goERP 'submit' page -->
 * {{ .Tools.StreamActionEvent "time-event-example" "12:00:00" }}
 * ```
 * Then from the POS the event can be received as follows
 * (This event will happen when someone opens the 'action' page)
 * ```ts
 * useGoErpEventStream('action', 'time-event-example', msg => {
 *   console.log(msg)
 * })
 * // logs { "name": "12:00:00" } whenever someone opens/refreshes the action page
 * ```
 */
export function useGoErpEventStream<T = any>(
  page: string,
  action: string,
  onMsg: (msg: T) => void,
) {
  const { url: apiEndpoint } = getEndpointForService('goerp');
  const goErpEndpoint = apiEndpoint?.replace('/api', '');
  const clientCode = useSelector(getClientCode);

  // Maintain reference to latest function
  // no need to force the caller to memoize this
  const onMsgRef = React.useRef(onMsg);
  React.useEffect(() => {
    onMsgRef.current = onMsg;
  }, [onMsg]);

  // Connect (/disconnect) from event source
  React.useEffect(() => {
    if (!goErpEndpoint) return () => undefined;

    const es = new EventSource(
      `${goErpEndpoint}${clientCode}/en/${page}?Form.Stream=true&Form.StreamAction=${action}`,
      { withCredentials: true },
    );
    es.addEventListener('content', event => {
      try {
        decodeGoErpContent(event.data).forEach(json => {
          onMsgRef.current((json as any) as T);
        });
      } catch (e) {
        if (REGEX_IS_404_RESPONSE_DATA.test(JSON.parse(event.data).data)) {
          console.warn('goERP stream 404 detected, closing connection', event);
          es.close();
        } else {
          console.warn('Unable to parse goERP event', event);
        }
      }
    });
    return () => es.close();
  }, [clientCode, page, action, goErpEndpoint]);
}
